import React, { useState } from 'react';
import { Users, Plus, X } from 'lucide-react';
import { useAlert } from '../../contexts/ConfirmContext';

// 초기 비밀번호는 서버가 고정으로("1234") 부여한다(services/settings.go의
// defaultSubAccountPassword) - 여기서는 아이디/이름/연락처만 받는다.
//
// 계정 추가/삭제는 오너(사장님 본인)만 할 수 있다 - 서버도 별도로
// 막지만(RequireOwner), 서브 계정으로 로그인한 사람에게 눌러도 항상
// 실패하는 버튼을 보여줄 이유가 없어 여기서도 숨긴다. 목록 조회는
// 오너가 아니어도 볼 수 있다.
const StoreUsers = ({ storeUsers, onAdd, onRemove, isOwner }) => {
  const notify = useAlert();
  const [newUser, setNewUser] = useState({ loginId: '', name: '', phone: '' });

  const addUser = async () => {
    if (!newUser.loginId.trim()) {
      await notify('로그인 아이디를 입력해주세요.');
      return;
    }
    if (!newUser.name.trim()) {
      await notify('이름을 입력해주세요.');
      return;
    }

    await onAdd(newUser.loginId.trim(), newUser.name.trim(), newUser.phone.trim());

    setNewUser({ loginId: '', name: '', phone: '' });
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-3 border-b">
        <h3 className="text-lg font-semibold flex items-center">
          <Users className="w-5 h-5 mr-2 text-gray-600" />
          관리자 웹 로그인 계정
        </h3>
      </div>

      <div className="p-3 space-y-6">
        {/* 계정 추가 폼 - 오너만 */}
        {isOwner && (
          <div className="border-2 border-dashed border-slate-300 rounded-xl p-3 bg-gradient-to-br from-slate-50 to-gray-50">
            <h4 className="text-lg font-medium text-slate-800 mb-4 flex items-center">
              <Plus className="w-5 h-5 mr-2 text-slate-600" />
              계정 추가
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">로그인 아이디</label>
                <input
                  type="text"
                  value={newUser.loginId}
                  onChange={(e) => setNewUser((prev) => ({ ...prev, loginId: e.target.value }))}
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 bg-white transition-colors"
                  placeholder="예: staff01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">이름</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 bg-white transition-colors"
                  placeholder="예: 김직원"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">연락처</label>
                <input
                  type="tel"
                  value={newUser.phone}
                  onChange={(e) => setNewUser((prev) => ({ ...prev, phone: e.target.value }))}
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 bg-white transition-colors"
                  placeholder="010-1234-5678"
                />
              </div>
            </div>

            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                💡 초기 비밀번호는 <strong>1234</strong>로 만들어집니다. 로그인 후 마이페이지에서 직접 바꿔야 합니다.
              </p>
            </div>

            <button
              onClick={addUser}
              className="w-full bg-gradient-to-r from-slate-600 to-slate-700 text-white py-3 px-4 rounded-lg hover:from-slate-700 hover:to-slate-800 transition-all duration-200 font-medium flex items-center justify-center shadow-lg hover:shadow-xl"
            >
              <Plus className="w-4 h-4 mr-2" />
              계정 추가하기
            </button>
          </div>
        )}

        {/* 계정 목록 */}
        <div>
          <h4 className="text-lg font-medium text-gray-800 mb-4">
            등록된 계정 ({(storeUsers || []).length}개)
          </h4>

          {(storeUsers || []).length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {storeUsers.map((user) => (
                <div key={user.id} className="bg-gradient-to-r from-slate-50 to-gray-50 border border-slate-200 rounded-lg p-3 relative">
                  {isOwner && (
                    <button
                      onClick={() => onRemove(user.id)}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}

                  <div className="pr-8 space-y-1">
                    <div className="font-medium text-gray-800">{user.name}</div>
                    <div className="text-sm text-gray-600">아이디: {user.loginId}</div>
                    {user.phone && <div className="text-sm text-gray-600">연락처: {user.phone}</div>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">등록된 계정이 없습니다</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoreUsers;
